/**
 * Receitas Page
 */

import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search } from "lucide-react";
import { ReceitasTable } from "@/components/receita/ReceitasTable";
import { ReceitaFormDialog } from "@/components/receita/ReceitaFormDialog";
import { ReceitaViewDialog } from "@/components/receita/ReceitaViewDialog";
import { ReceitaDeleteDialog } from "@/components/receita/ReceitaDeleteDialog";
import { receitasAdapter } from "@/data";
import { useToast } from "@/hooks/use-toast";
import { gerarPdfReceita } from "@/lib/pdf/pdfGenerator";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentTenantId } from "@/lib/tenant";
import type { Receita, ReceitaCompleta } from "@/types/receita";

export default function Receitas() {
  const [search, setSearch] = useState("");
  const [receitas, setReceitas] = useState<Receita[]>([]);
  const [categoriaFilter, setCategoriaFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [selectedReceita, setSelectedReceita] = useState<ReceitaCompleta | null>(null);
  const { toast } = useToast();

  const loadReceitas = async () => {
    try {
      setIsLoading(true);
      const filters: any = {};
      
      if (categoriaFilter !== "all") filters.categoria = categoriaFilter;

      const response = await receitasAdapter.list({
        search,
        filters,
        sort: "created_at",
        order: "desc",
      });

      setReceitas(response.items || []);
    } catch (error) {
      console.error("Erro ao carregar receitas:", error);
      setReceitas([]);
      // Não mostra toast para tabela vazia - apenas para erros reais
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReceitas();
  }, [search, categoriaFilter]);

  const handleCreate = () => {
    setFormMode("create");
    setSelectedReceita(null);
    setIsFormOpen(true);
  };

  const handleEdit = async (receita: Receita) => {
    try {
      const receitaCompleta = await receitasAdapter.get(receita.id_receita);
      if (receitaCompleta) {
        setSelectedReceita(receitaCompleta);
        setFormMode("edit");
        setIsFormOpen(true);
      }
    } catch (error) {
      console.error("Erro ao carregar receita:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados da receita.",
        variant: "destructive",
      });
    }
  };

  const handleView = async (receita: Receita) => {
    try {
      const receitaCompleta = await receitasAdapter.get(receita.id_receita);
      if (receitaCompleta) {
        setSelectedReceita(receitaCompleta);
        setIsViewOpen(true);
      }
    } catch (error) {
      console.error("Erro ao carregar receita:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados da receita.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (receita: Receita) => {
    try {
      const receitaCompleta = await receitasAdapter.get(receita.id_receita);
      if (receitaCompleta) {
        setSelectedReceita(receitaCompleta);
        setIsDeleteOpen(true);
      }
    } catch (error) {
      console.error("Erro ao carregar receita:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados da receita.",
        variant: "destructive",
      });
    }
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    loadReceitas();
    toast({
      title: formMode === "create" ? "Receita criada" : "Receita atualizada",
      description: `Receita ${formMode === "create" ? "criada" : "atualizada"} com sucesso.`,
    });
  };

  const handleDeleteSuccess = () => {
    setIsDeleteOpen(false);
    loadReceitas();
    toast({
      title: "Receita excluída",
      description: "Receita excluída com sucesso.",
    });
  };

  const handlePrint = async (receita: Receita) => {
    try {
      // Buscar receita completa
      const receitaCompleta = await receitasAdapter.get(receita.id_receita);
      if (!receitaCompleta) {
        throw new Error("Receita não encontrada");
      }

      // Buscar dados do tenant
      const tenantId = await getCurrentTenantId();
      const tenantResponse = await supabase
        .from("tenant")
        .select("name")
        .eq("id", tenantId)
        .single();
      
      const nomeEmpresa = tenantResponse.data?.name || "Empresa";

      // Buscar dados do usuário
      const { data: { user } } = await supabase.auth.getUser();
      const profileResponse = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user?.id)
        .single();
      
      const nomeUsuario = profileResponse.data?.full_name || user?.email || "Usuário";

      // Gerar PDF
      const result = await gerarPdfReceita(receitaCompleta, nomeEmpresa, nomeUsuario);
      
      if (result.success) {
        toast({
          title: "PDF gerado com sucesso",
          description: "O arquivo foi baixado no seu navegador.",
        });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      toast({
        title: "Erro ao gerar PDF",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    }
  };

  // Extrair categorias únicas
  const categorias = Array.from(new Set(receitas.map((r) => r.categoria).filter(Boolean)));

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Receitas</h1>
            <p className="text-muted-foreground">Gerencie suas receitas de produção</p>
          </div>
          <Button onClick={handleCreate} className="bg-yellow-500 hover:bg-yellow-600 text-black">
            <Plus className="mr-2 h-4 w-4" />
            Nova Receita
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-end">
          <div className="flex-1 max-w-sm">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar receitas..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <Select value={categoriaFilter} onValueChange={setCategoriaFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas Categorias</SelectItem>
              {categorias.map((cat) => (
                <SelectItem key={cat} value={cat!}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <ReceitasTable
          receitas={receitas}
          isLoading={isLoading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onPrint={handlePrint}
        />

        {/* Dialogs */}
        <ReceitaFormDialog
          open={isFormOpen}
          onOpenChange={setIsFormOpen}
          mode={formMode}
          receita={selectedReceita}
          onSuccess={handleFormSuccess}
        />

        <ReceitaViewDialog
          open={isViewOpen}
          onOpenChange={setIsViewOpen}
          receita={selectedReceita}
          onEdit={() => {
            setIsViewOpen(false);
            if (selectedReceita) {
              handleEdit(selectedReceita.receita);
            }
          }}
        />

        <ReceitaDeleteDialog
          open={isDeleteOpen}
          onOpenChange={setIsDeleteOpen}
          receita={selectedReceita}
          onSuccess={handleDeleteSuccess}
        />
      </div>
    </Layout>
  );
}
